/**
 * Booking policy for the farm stay: the booking window, the deposit split, the
 * cancellation ladder and the day-visit tiers.
 *
 * Source of truth is Ananya's answersheet of 2026-08-29. It lives here rather
 * than being retyped into page copy because the same numbers appear in four
 * places -- the itinerary page, the booking wizard, the review screen and the
 * WhatsApp message Ananya receives -- and they must not drift apart.
 *
 * Everything reads from the StayPackage row when it is set, and falls back to
 * these defaults when it is not, so the site is never blank on a policy while
 * a seed catches up.
 */

/** Fallbacks. The seeded package carries the same numbers. */
export const POLICY_DEFAULTS = {
  childRatePercent: 50,
  minNoticeDays: 3,
  maxAdvanceMonths: 3,
  depositPercent: 50,
  mealChoiceCutoffDays: 3,
};

export const policyOf = (pkg) => ({
  childRatePercent: pkg?.childRatePercent ?? POLICY_DEFAULTS.childRatePercent,
  minNoticeDays: pkg?.minNoticeDays ?? POLICY_DEFAULTS.minNoticeDays,
  maxAdvanceMonths: pkg?.maxAdvanceMonths ?? POLICY_DEFAULTS.maxAdvanceMonths,
  depositPercent: pkg?.depositPercent ?? POLICY_DEFAULTS.depositPercent,
  mealChoiceCutoffDays: pkg?.mealChoiceCutoffDays ?? POLICY_DEFAULTS.mealChoiceCutoffDays,
});

/* ------------------------------------------------------------------ dates */

/**
 * Local Y/M/D reinterpreted as UTC midnight, which is how every date in this
 * feature is stored. Going through `toISOString()` on a plain `new Date()`
 * would hand back yesterday for anyone in IST before 05:30.
 */
const utcMidnight = (d) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
export const isoDay = (d) => utcMidnight(d).toISOString().slice(0, 10);

const shiftDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const shiftMonths = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  // 31 Jan + 1 month is 28/29 Feb, not 3 March.
  const lastOfMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastOfMonth));
  return d.toISOString().slice(0, 10);
};

/** Whole days from `from` to `to`, negative if `to` is in the past. */
export const daysBetween = (from, to) => {
  if (!from || !to) return 0;
  return Math.round(
    (new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86400000,
  );
};

/**
 * The arrival dates a guest may pick: at least `minNoticeDays` away and at most
 * `maxAdvanceMonths` out. Returned as ISO strings so the picker can compare
 * them with plain string comparison, which is what it already does for `taken`.
 */
export const bookingWindow = (pkg, today = new Date()) => {
  const { minNoticeDays, maxAdvanceMonths } = policyOf(pkg);
  const todayIso = isoDay(today);
  return {
    earliest: shiftDays(todayIso, minNoticeDays),
    latest: shiftMonths(todayIso, maxAdvanceMonths),
    minNoticeDays,
    maxAdvanceMonths,
  };
};

const prettyDay = (iso) =>
  (iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  }) : '');

/** Why this arrival date is not bookable, in words, or null if it is fine. */
export const arrivalWindowError = (pkg, startDate, today = new Date()) => {
  if (!startDate) return null;
  const { earliest, latest, minNoticeDays, maxAdvanceMonths } = bookingWindow(pkg, today);
  if (startDate < earliest) {
    return `We need at least ${minNoticeDays} days' notice, so the earliest arrival is ${prettyDay(earliest)}.`;
  }
  if (startDate > latest) {
    return `Bookings open ${maxAdvanceMonths} months ahead, so the latest arrival is ${prettyDay(latest)}.`;
  }
  return null;
};

/** When meal choices stop being changeable. */
export const mealCutoffDate = (pkg, startDate) => {
  const { mealChoiceCutoffDays } = policyOf(pkg);
  return startDate ? shiftDays(startDate, -mealChoiceCutoffDays) : null;
};

/* --------------------------------------------------------------- money out */

/**
 * The cancellation ladder. Ordered most generous first; `refundPercentFor`
 * walks it and takes the first rule the guest still qualifies for, so adding a
 * tier is one line and cannot leave a gap.
 */
export const CANCELLATION_TIERS = [
  { minDaysBefore: 3, refundPercent: 100, label: '3 or more days before check-in', detail: 'Full refund' },
  { minDaysBefore: 2, refundPercent: 50, label: '2 days before check-in', detail: 'Half the amount is refunded' },
  { minDaysBefore: 0, refundPercent: 0, label: '1 day before, on the day, or a no-show', detail: 'No refund' },
];

/**
 * @param daysBefore whole days between cancelling and check-in. Negative (after
 *                   arrival) is treated the same as a no-show.
 */
export const refundPercentFor = (daysBefore) => {
  const d = Number.isFinite(daysBefore) ? daysBefore : 0;
  const tier = CANCELLATION_TIERS.find((t) => d >= t.minDaysBefore);
  return tier ? tier.refundPercent : 0;
};

/**
 * A day visit is arranged for that specific day -- the kitchen shops for it and
 * the Baul artist is booked -- so its advance is not refundable at all. This is
 * deliberately not part of the ladder above.
 */
export const DAY_VISIT_REFUND_PERCENT = 0;

/** The 50/50 split. Rounded so the two halves always add back to the total. */
export const depositSplit = (total, pkg) => {
  const percent = policyOf(pkg).depositPercent;
  const amount = Math.round((Number(total) || 0) * (percent / 100));
  return { percent, dueNow: amount, dueAtCheckout: (Number(total) || 0) - amount };
};

/* ----------------------------------------------------------- day visits */

/**
 * Tiers come off the package as JSON. Anything malformed is dropped rather than
 * thrown, because a bad seed should cost the day-visit option, not the page.
 *
 * The unwrap loop is not paranoia. AWSJSON serialises whatever sits in the
 * DynamoDB attribute, so seeding a `json.dumps(...)` STRING rather than a
 * native list reaches the client double-encoded: one parse hands back another
 * string, `Array.isArray` says no, and the whole day-visit section vanishes
 * with nothing in the console. That shipped once. Unwrap until it is not a
 * string, bounded so a pathological value cannot spin.
 */
export const dayVisitTiers = (pkg) => {
  const raw = pkg?.dayVisitTiers;
  if (!raw) return [];
  let parsed = raw;
  for (let depth = 0; typeof parsed === 'string' && depth < 3; depth += 1) {
    try { parsed = JSON.parse(parsed); } catch { return []; }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter((t) => t && t.id && t.label && Number.isFinite(Number(t.price)))
    .map((t, i) => ({
      id: String(t.id),
      label: String(t.label),
      description: t.description ? String(t.description) : '',
      includes: Array.isArray(t.includes) ? t.includes.map(String) : [],
      price: Number(t.price),
      sortOrder: Number.isFinite(Number(t.sortOrder)) ? Number(t.sortOrder) : i,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
};

/* ------------------------------------------------------------------ prose */

/**
 * The house rules, in Ananya's words. Kept as data so the itinerary page and
 * the booking wizard show the same sentences.
 */
export const HOUSE_NOTES = [
  {
    id: 'whole-farm',
    title: 'You have the whole farm',
    body: 'One booking takes the entire farm — there are no other guests. Up to 4 people, '
      + 'or 4 adults with one child aged 6 or under.',
  },
  {
    id: 'not-a-restaurant',
    title: 'A farm kitchen, not a restaurant',
    body: 'There is no fixed menu and no food on demand. Everything is simple Bengali home '
      + 'cooking made from what is fresh that day. Tell us what you like and we cook around '
      + 'it — we will let you know the evening before roughly what is coming.',
  },
  {
    id: 'baul',
    title: 'One Baul evening is included',
    body: 'A local Baul artist performs for you once during your stay. On a longer stay we can '
      + 'arrange more evenings if the artists are free — those are charged at whatever the '
      + 'artists themselves charge, with nothing added.',
  },
  {
    id: 'diets',
    title: 'Vegetarian is easy',
    body: 'A completely vegetarian stay is no trouble at all. We are not set up for vegan or '
      + 'Jain cooking, so we would rather say so now than disappoint you here.',
  },
];
